import fetchData from "@/utils/fetchData";
import updateCurrentPage from "@/utils/updateCurrentPage";

// Older versions stored the id JSON encoded, newer ones store it as is
function readStoredUserId() {
  const stored = localStorage.getItem("userId");
  if (!stored) return "";
  try {
    const parsed = JSON.parse(stored);
    return typeof parsed === "string" ? parsed : stored;
  } catch {
    return stored;
  }
}

export default {
  name: "auth",
  namespaced: true,
  state: {
    loggedIn: false,
    loading: false,
    loadingItem: "",
    error: "",
    userId: "",
    campaignLink: "",
  },
  mutations: {
    SET_LOGGED_IN(state, payload) {
      state.loggedIn = payload;
      localStorage.setItem("loggedIn", payload);
    },
    SET_USER_ID(state, payload) {
      state.userId = payload;
    },
    SET_ERROR(state, payload) {
      state.error = payload;
    },
    AUTH_REQUEST(state, payload) {
      state.loading = true;
      state.loadingItem = payload;
      state.error = "";
    },
    AUTH_SUCCESS(state, { userId }) {
      state.loading = false;
      state.loadingItem = "";
      state.userId = userId;
      localStorage.setItem("userId", userId);
    },
    AUTH_ERROR(state, payload) {
      state.loading = false;
      state.loadingItem = "";
      state.error = payload;
    },
    SET_CAMPAIGN_LINK(state, payload) {
      state.campaignLink = payload;
    },
  },
  actions: {
    // Runs once as the app starts
    init({ dispatch, state }) {
      dispatch("setUserID");
      if (state.loggedIn) {
        dispatch("personalInfo/fetchInfo", null, { root: true });
        dispatch("tours/fetchTours", null, { root: true });
      }
    },
    clearError({ commit }) {
      commit("SET_ERROR", "");
    },
    setCampaignLink({ commit }, payload) {
      commit("SET_CAMPAIGN_LINK", payload);
      fetchData("user/triggerCampaign", { campaign: payload }, false, false);
    },
    authSuccess({ commit, dispatch }, { userId }) {
      commit("AUTH_SUCCESS", { userId });
      commit("SET_LOGGED_IN", true);
      commit("modals/SET_AUTH_OPEN", false, { root: true });
      dispatch("personalInfo/fetchInfo", null, { root: true });
      dispatch("tours/fetchTours", null, { root: true });
      dispatch("sendUserDetails");
    },
    // The server no longer recognises our session, e.g. it expired
    sessionExpired({ commit, state }) {
      if (state.loggedIn) {
        commit("SET_LOGGED_IN", false);
      }
    },
    async sendUserDetails({ dispatch, state }) {
      const [country, browser] = await Promise.all([
        dispatch("getCountry"),
        dispatch("getBrowserDetails"),
      ]);
      const info = { browser };
      if (country) {
        info.country = country;
      }
      if (state.campaignLink) {
        info.campaignLink = state.campaignLink;
      }
      fetchData("user/setData", { info }, false, false, false);
    },
    setUserID({ commit, state }) {
      if (state.userId) {
        return;
      }
      let userId = readStoredUserId();
      if (!userId) {
        userId = crypto.randomUUID();
        localStorage.setItem("userId", userId);
      }
      commit("SET_USER_ID", userId);
      if (localStorage.getItem("loggedIn") === "true") {
        commit("SET_LOGGED_IN", true);
      }
    },
    async startFederatedLogin({ commit, dispatch, rootState }, provider) {
      commit("AUTH_REQUEST", provider);
      await dispatch("setUserID");
      // Save the page so it can be restored when the provider sends the user back
      await updateCurrentPage(rootState);
      const returnTo = encodeURIComponent(window.location.origin);
      if (provider === "Google") {
        return `${process.env.VUE_APP_APIURL}/api/v1/user/login/federated/google?returnTo=${returnTo}`;
      }
      commit("AUTH_ERROR", "That login method isn't available");
      return null;
    },
    async failedFederatedLogin({ commit }) {
      commit("AUTH_ERROR", "An error occurred, please try again");
    },
    async successfulFederatedLogin({ commit, dispatch }) {
      const { response } = await fetchData("user/setId", {}, false, false);
      if (response.status === 201) {
        dispatch("authSuccess", { userId: response.result.userId });
      } else {
        commit("AUTH_ERROR", "An error occurred, please try again");
      }
      return response;
    },
    async logout({ commit, dispatch }) {
      const { response } = await fetchData(
        "user/logout",
        {},
        false,
        false,
        false
      );
      if (response.status === 0) {
        commit("AUTH_ERROR", "An error occurred, please try again");
        return;
      }
      localStorage.removeItem("userId");
      commit("SET_LOGGED_IN", false);
      commit("SET_USER_ID", "");
      dispatch("personalInfo/reset", null, { root: true });
      dispatch("setUserID");
    },
    getBrowserDetails() {
      return {
        userAgent: navigator.userAgent,
        vendor: navigator.vendor,
        platform: navigator.platform,
        language: navigator.language,
        languages: [...(navigator.languages || [])],
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: navigator.deviceMemory,
        maxTouchPoints: navigator.maxTouchPoints,
        doNotTrack: navigator.doNotTrack,
        connection: navigator.connection
          ? {
              effectiveType: navigator.connection.effectiveType,
              downlink: navigator.connection.downlink,
              rtt: navigator.connection.rtt,
            }
          : null,
      };
    },
    async getCountry() {
      try {
        const response = await fetch("https://ipapi.co/json/");
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    },
  },
  modules: {},
  getters: {},
};
